import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils/utils";

const buttonVariants = cva(
  "inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap  font-medium transition-all disabled:pointer-events-none  disabled:shadow-xs disabled:text-black-500 disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none  aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-blue-normal text-white  hover:bg-blue-normal-hover hover:underline  dark:focus-visible:ring-blue-primary-400/40 disabled:bg-black-100  focus-visible:ring-blue-primary-100 focus-visible:ring-[3px]",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-black-100 bg-white shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-white  text-blue-normal border border-blue-normal hover:text-blue-normal-hover hover:underline hover:border-blue-normal-hover dark:hover:bg-blue-primary-50/20 dark:hover:text-blue-primary-400 disabled:border-black-500 focus-visible:border-blue-normal focus-visible:ring-blue-primary-100 focus-visible:ring-[3px]",
        ghost: " hover:text-blue-darker hover:underline transition-all",
        link: "text-green-normal underline-offset-4 hover:underline hover:text-green-normal-active disabled:text-black-500 focus-visible:ring-green-light focus-visible:ring-[3px]",
        alternative:
          "bg-green-normal text-black-500  hover:bg-green-normal-hover hover:underline  dark:focus-visible:ring-green-light-400/40 disabled:bg-black-100 disabled:text-black-500 disabled:opacity-50 focus-visible:ring-green-light focus-visible:ring-[3px]",
        glass:
          "bg-white/10 text-h3 text-green-normal hover:bg-white/15 active:scale-95 disabled:text-white/50 focus-visible:ring-ring focus-visible:ring-[3px] shadow-sm backdrop-blur-sm border border-white/20",

        "destructive-glass":
          "bg-white/10 text-h3 text-white hover:bg-white/15 active:scale-95 disabled:text-white/50 focus-visible:ring-ring focus-visible:ring-[3px] shadow-sm backdrop-blur-sm border border-white/20",
      },
      size: {
        default: "h-12 rounded-3xl px-10  py-3   has-[>svg]:py-3",
        sm: "h-10 rounded-3xl gap-1.5 px-6  py-2 has-[>svg]:py-2.5",
        md: "h-12 rounded-3xl px-10 py-3  has-[>svg]:py-3",
        lg: "h-12 rounded-3xl px-10  py-3 lg:h-15 lg:rounded-full lg:px-12 lg:py-4  has-[>svg]:py-4",
        icon: " rounded-[10px] has-[>svg]:size-12 has-[>svg]:px-0",
        glass: "px-4 py-2 rounded-[12px] gap-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
